import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiHeart,
  FiLock,
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../Datas/siteConfig";
import "../css/cartpage.scss";

const formatPrice = (value) =>
  new Intl.NumberFormat(siteConfig.locale, {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(value) || 0);

const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

function CartPage() {
  const navigate = useNavigate();
  const cart = useContext(CartContext);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const freeShippingThreshold = Math.max(
    Number(siteConfig.freeShippingThreshold) || 0,
    0
  );

  const freeShippingRemaining = Math.max(
    freeShippingThreshold - cart.cartSubtotal,
    0
  );
  const shippingProgress =
    freeShippingThreshold === 0
      ? 100
      : Math.min((cart.cartSubtotal / freeShippingThreshold) * 100, 100);

  const saveForLater = (item) => {
    cart.addToWishlist(item.id);
    cart.removeFromCart(item.id, item.size, item.color);
  };

  if (cart.cartItems.length === 0) {
    return (
      <main className="bag bag--empty">
        <section className="bag-empty" aria-labelledby="empty-bag-title">
          <p className="bag-eyebrow">Your basket</p>
          <h1 id="empty-bag-title">Your basket is empty</h1>
          <p>
            The trail is still open. Explore dependable layers, footwear and
            equipment for your next day outside.
          </p>
          <div className="bag-empty__actions">
            <Link to="/shop" onClick={toTop}>Shop all products</Link>
            <Link to="/shop?tag=sale" onClick={toTop}>Explore the sale</Link>
          </div>
        </section>
        <section className="bag-benefits" aria-label="Shopping benefits">
          <div><FiTruck aria-hidden="true" /><span><strong>Tracked delivery</strong>Flat rate {formatPrice(siteConfig.shippingCost)}</span></div>
          <div><FiRefreshCw aria-hidden="true" /><span><strong>Free returns</strong>Within {siteConfig.returnsWindow} days</span></div>
          <div><FiShield aria-hidden="true" /><span><strong>Expert support</strong>Advice when you need it</span></div>
        </section>
      </main>
    );
  }

  return (
    <main className="bag">
      <nav className="bag-breadcrumb" aria-label="Breadcrumb">
        <Link to="/" onClick={toTop}>Home</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Shopping basket</span>
      </nav>

      <header className="bag-header">
        <p className="bag-eyebrow">Ready for the trail</p>
        <h1>Your shopping basket</h1>
        <span>{cart.cartCount} {cart.cartCount === 1 ? "item" : "items"}</span>
      </header>

      <div className="bag-layout">
        <section className="bag-items" aria-label="Basket items">
          <div className="bag-shipping-meter">
            <div>
              {freeShippingRemaining > 0 ? (
                <p>Spend <strong>{formatPrice(freeShippingRemaining)}</strong> more for free standard delivery.</p>
              ) : (
                <p><FiCheck aria-hidden="true" /> Your order qualifies for <strong>free standard delivery.</strong></p>
              )}
              <span>{Math.round(shippingProgress)}%</span>
            </div>
            <span
              className="bag-shipping-meter__track"
              role="progressbar"
              aria-label="Progress towards free standard delivery"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(shippingProgress)}
            >
              <span style={{ width: `${shippingProgress}%` }} />
            </span>
          </div>

          {cart.cartItems.map((item) => {
            const product = productData.find((candidate) => candidate.id === item.id);
            if (!product) return null;
            const itemTotal = product.price * item.quantity;

            return (
              <article className="bag-item" key={`${item.id}-${item.size}-${item.color}`}>
                <Link className="bag-item__media" to={`/shopDetails/${product.id}`} onClick={toTop}>
                  <img src={product.image} alt={product.name} loading="lazy" decoding="async" />
                  {product.badge && <span>{product.badge}</span>}
                </Link>

                <div className="bag-item__content">
                  <div className="bag-item__heading">
                    <div>
                      <p>{product.category}</p>
                      <h2><Link to={`/shopDetails/${product.id}`} onClick={toTop}>{product.name}</Link></h2>
                      <dl>
                        <div><dt>Colour</dt><dd>{item.color}</dd></div>
                        <div><dt>Size</dt><dd>{item.size}</dd></div>
                      </dl>
                    </div>
                    <strong>{formatPrice(itemTotal)}</strong>
                  </div>

                  <div className="bag-item__footer">
                    <div className="bag-quantity" aria-label={`Quantity for ${product.name}`}>
                      <button type="button" aria-label={`Decrease ${product.name} quantity`} disabled={item.quantity <= 1} onClick={() => cart.updateQuantity(item.id, item.size, item.color, item.quantity - 1)}><FiMinus aria-hidden="true" /></button>
                      <span aria-live="polite">{item.quantity}</span>
                      <button type="button" aria-label={`Increase ${product.name} quantity`} disabled={item.quantity >= product.stock} onClick={() => cart.updateQuantity(item.id, item.size, item.color, item.quantity + 1)}><FiPlus aria-hidden="true" /></button>
                    </div>
                    <div className="bag-item__actions">
                      <button type="button" onClick={() => saveForLater(item)}><FiHeart aria-hidden="true" /> Save for later</button>
                      <button type="button" onClick={() => cart.removeFromCart(item.id, item.size, item.color)}><FiTrash2 aria-hidden="true" /> Remove</button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}

          <div className="bag-continue">
            <Link to="/shop" onClick={toTop}><span aria-hidden="true">←</span> Continue shopping</Link>
            <div className="bag-clear">
              {confirmingClear && <span role="status">Remove every item?</span>}
              <button
                type="button"
                onClick={() => {
                  if (confirmingClear) {
                    cart.clearCart();
                  } else {
                    setConfirmingClear(true);
                  }
                }}
              >
                {confirmingClear ? "Yes, clear basket" : "Clear basket"}
              </button>
              {confirmingClear && (
                <button type="button" onClick={() => setConfirmingClear(false)}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </section>

        <aside className="bag-summary" aria-labelledby="order-summary-title">
          <h2 id="order-summary-title">Order summary</h2>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(cart.cartSubtotal)}</dd></div>
            <div><dt>Standard delivery</dt><dd>{cart.shippingCost === 0 ? "Free" : formatPrice(cart.shippingCost)}</dd></div>
            <div className="bag-summary__total"><dt>Total <small>Taxes shown where applicable</small></dt><dd>{formatPrice(cart.orderTotal)}</dd></div>
          </dl>

          <p className="bag-summary__promotion">
            Eligible promotional savings are shown in product prices and included
            in this total.
          </p>

          <button className="bag-checkout" type="button" onClick={() => { navigate("/checkout"); window.scrollTo({ top: 0 }); }}>
            Secure checkout <FiArrowRight aria-hidden="true" />
          </button>
          <p className="bag-summary__secure"><FiLock aria-hidden="true" /> Encrypted and secure checkout</p>
          <ul className="bag-summary__payments" aria-label="Accepted payments"><li>VISA</li><li>Mastercard</li><li>AMEX</li></ul>
          <div className="bag-summary__help">
            <p>Need help with your order?</p>
            <Link to="/contact" onClick={toTop}>Contact our outdoor experts</Link>
          </div>
        </aside>
      </div>

      <section className="bag-footnotes" aria-label="Delivery and returns information">
        <Link to="/shipping" onClick={toTop}><FiTruck aria-hidden="true" /><span><strong>Delivery options</strong>Standard and express services available</span><FiArrowRight aria-hidden="true" /></Link>
        <Link to="/returns" onClick={toTop}><FiRefreshCw aria-hidden="true" /><span><strong>{siteConfig.returnsWindow}-day returns</strong>Simple return support by email</span><FiArrowRight aria-hidden="true" /></Link>
      </section>
    </main>
  );
}

export default CartPage;
